import { Fragment, useState } from "react";
import type { BridgeRow as Row, Side } from "./bridgeModel";
import { groupEdiRows } from "./bridgeModel";
import { BridgeRow } from "./BridgeRow";

const GRID = { display: "grid", gridTemplateColumns: "1fr 150px 1fr", gap: "10px 8px", alignItems: "center", padding: "12px 4px" } as const;

export function MappingBridge({
  rows, leftSide, onAddMapping, onOpenEntry, onAddBoSource,
}: {
  rows: Row[];
  leftSide: Side;
  onAddMapping: (row: Row) => void;
  onOpenEntry: (mappingId: string) => void;
  onAddBoSource: (row: Row) => void;
}) {
  // EDI perspective rows carry segmentGroup; BO rows don't.
  const isEdi = rows.length > 0 && rows[0].segmentGroup !== undefined;

  if (isEdi) {
    return <EdiHierarchy rows={rows} leftSide={leftSide} onAddMapping={onAddMapping} onOpenEntry={onOpenEntry} onAddBoSource={onAddBoSource} />;
  }
  return <FlatGroups rows={rows} leftSide={leftSide} onAddMapping={onAddMapping} onOpenEntry={onOpenEntry} onAddBoSource={onAddBoSource} />;
}

function FlatGroups({
  rows, leftSide, onAddMapping, onOpenEntry, onAddBoSource,
}: {
  rows: Row[];
  leftSide: Side;
  onAddMapping: (row: Row) => void;
  onOpenEntry: (mappingId: string) => void;
  onAddBoSource: (row: Row) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  // group rows by row.group, emit a header row spanning all 3 columns
  const groups: Array<{ name: string; items: Row[] }> = [];
  for (const r of rows) {
    const last = groups[groups.length - 1];
    if (last && last.name === r.group) last.items.push(r);
    else groups.push({ name: r.group, items: [r] });
  }

  return (
    <div style={GRID}>
      {groups.map((g) => (
        <Fragment key={g.name}>
          <div style={{ gridColumn: "1 / 4", marginTop: "6px" }}>
            <span style={{ fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", color: "var(--sapContent_LabelColor)" }}>{g.name}</span>
          </div>
          {g.items.map((r) => (
            <BridgeRow
              key={r.key}
              row={r}
              leftSide={leftSide}
              expanded={expanded === r.key}
              onToggle={() => setExpanded(expanded === r.key ? null : r.key)}
              onAddMapping={onAddMapping}
              onOpenEntry={onOpenEntry}
              onAddBoSource={onAddBoSource}
            />
          ))}
        </Fragment>
      ))}
    </div>
  );
}

function EdiHierarchy({
  rows, leftSide, onAddMapping, onOpenEntry, onAddBoSource,
}: {
  rows: Row[];
  leftSide: Side;
  onAddMapping: (row: Row) => void;
  onOpenEntry: (mappingId: string) => void;
  onAddBoSource: (row: Row) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const blocks = groupEdiRows(rows);

  // collapse state keyed by the block's unique key; collapsed by default when no mappings.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const g of blocks) {
      for (const s of g.segments) {
        init[s.key] = s.mappedCount === 0;
      }
    }
    return init;
  });

  return (
    <div style={GRID}>
      {blocks.map((g) => (
        <Fragment key={g.segmentGroup}>
          <div style={{ gridColumn: "1 / 4", marginTop: "10px" }}>
            <span style={{ fontWeight: 700, fontSize: "0.8rem", color: "var(--sapContent_LabelColor)" }}>{g.segmentGroup}</span>
          </div>
          {g.segments.map((s) => {
            const ckey = s.key;
            const isCollapsed = collapsed[ckey] ?? false;
            return (
              <Fragment key={ckey}>
                <div
                  data-testid={`seg-${ckey}`}
                  style={{ gridColumn: "1 / 4", paddingLeft: 16, marginTop: "4px", display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <span
                    data-testid={`segtoggle-${ckey}`}
                    onClick={() => setCollapsed((c) => ({ ...c, [ckey]: !isCollapsed }))}
                    style={{ cursor: "pointer", userSelect: "none", fontSize: "0.8rem", color: "var(--sapContent_LabelColor)" }}
                  >
                    {isCollapsed ? "▸" : "▾"}
                  </span>
                  <span style={{ fontWeight: 600, fontSize: "0.78rem" }}>
                    {s.segName ? `${s.segment} — ${s.segName}` : s.segment}
                  </span>
                  <span style={{ fontSize: "0.68rem", padding: "1px 6px", borderRadius: "999px", background: "var(--sapList_Background)", border: "1px solid var(--sapList_BorderColor)", color: "var(--sapContent_LabelColor)" }}>
                    {s.mappedCount}/{s.rows.length}
                  </span>
                </div>
                {!isCollapsed &&
                  s.rows.map((r) => (
                    <BridgeRow
                      key={r.key}
                      row={r}
                      leftSide={leftSide}
                      expanded={expanded === r.key}
                      onToggle={() => setExpanded(expanded === r.key ? null : r.key)}
                      onAddMapping={onAddMapping}
                      onOpenEntry={onOpenEntry}
                      onAddBoSource={onAddBoSource}
                    />
                  ))}
              </Fragment>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}
